import React, { useState, useMemo, useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import shortid from 'shortid'
import numeral from 'numeraljs'
import classNames from 'classnames'

import Modal, { ModalHead, ModalBody, ModalFooter } from './modal'
import Heading from './heading'
import ConfirmationButtons from './confirmation-buttons'
// import ToolTip from './tooltip'
import { Table, Body, Head, HeaderCell, Cell, BodyItem } from './row'

import { ClickEditInput, SearchInput } from './input'
import styles from '../component-styles/campaign-codes-administration.module.scss'
import Button, { ButtonRow } from './button'
import { GeneratorFields, GeneratorSelectFields } from '../api-client/api-types'
import {
  updateGeneratorParameterSelectUpdateAsync,
  updateGeneratorParameterSelectDeleteAsync,
} from '../store/actions'
import DeleteButtonWithConfirmation from './delete-button-with-confirmation'
import {
  getItemByKeyValue,
  getValidationChecksObject,
  prepareInput,
} from '../helpers'
import { SelectBoxSimple, MultiSelectBox } from './select-box'
import AddNewSelectField from './campaign-codes-add-new-select-field'
import useMobile from '../hooks/useMobile'
import useTableSortFilter from '../hooks/useTableSortFilter'
import { CampaignCodesState } from '../store/campaign-codes-reducer'
import HeaderPanel, { Panel } from './header-panel'
import OrderArrow from './order-arrow'
import Pagination from './pagination'
import ToolTip from './tooltip'
import hide from '../assets/icon-eye-open.svg'
import hidden from '../assets/icon-eye-crossed-inactive.svg'
import deleteImg from '../assets/bin.svg'

interface SelectTableProps {
  fields: GeneratorSelectFields[]
  hasChildren: boolean
  fieldName: string
  onUpdateField: any
  onDelete: any
  showAdd: boolean
  setShowAdd: (showAdd: boolean) => void
  restrictDropdowns: boolean
  parentsList: GeneratorFields[]
  parentID?: string
  updateGeneratorParam: (
    updatedField: GeneratorSelectFields | GeneratorSelectFields[],
  ) => void
  fieldID: string | undefined
  forceLowerCase: boolean
}

export default function SelectTable({
  fields,
  hasChildren,
  fieldName,
  onUpdateField,
  onDelete,
  showAdd,
  setShowAdd,
  restrictDropdowns,
  parentsList,
  updateGeneratorParam,
  parentID = '',
  fieldID,
  forceLowerCase,
}: SelectTableProps): React.ReactElement {
  const dispatch = useDispatch()
  const { copy } = useSelector((s: any) => s.user)
  const { generatedStructure } = useSelector(
    (s: any): CampaignCodesState => s.campaignCodes,
  )

  // Reverse by order received from API on load
  const [fieldsCorrectOrder, setFieldsCorrectOrder] = useState<
    GeneratorSelectFields[] | undefined
  >()

  const {
    initialSort,
    setInitialSort,
    orderAsc,
    sortKey,
    setSortOrder,
    pages,
    activePage,
    setActivePage,
    setSearchTerm,
    count,
    orderedList: orderedFields,
    total,
  } = useTableSortFilter(fieldsCorrectOrder, '', 'optionName', 25)

  useEffect(() => {
    if (initialSort) {
      setFieldsCorrectOrder(JSON.parse(JSON.stringify(fields)).reverse())
    } else {
      setFieldsCorrectOrder(fields)
    }
  }, [initialSort, parentsList])

  const [parentFieldID, setParentFieldID] = useState(parentID)
  const [active, setActive] = useState(false)
  const isMobile = useMobile(769)

  const toggleActive = () => {
    setActive(!active)
  }

  const validation = useMemo(() => {
    return getValidationChecksObject(generatedStructure, false, [
      {
        name: 'CONVERT_LOWER_CASE',
        enabled: forceLowerCase,
      },
    ])
  }, [generatedStructure, forceLowerCase])

  if (!fields && !showAdd) {
    return <></>
  }

  const selectedParent: null | GeneratorFields = useMemo(() => {
    if (parentFieldID === '') {
      return null
    }
    return getItemByKeyValue(parentsList, 'fieldID', parentFieldID)
  }, [parentFieldID])

  const SelectParent = (
    <>
      <span>Only show if</span>
      <SelectBoxSimple
        className={styles.selectWrapper}
        name={`select-${fieldName}`}
        value={parentFieldID}
        onChange={(val) => {
          if (hasChildren) {
            setActive(true)
          } else {
            setParentFieldID(val)
          }
        }}
      >
        <>
          <option value="">None</option>
          {parentsList.map((item: GeneratorFields) => {
            return (
              <option key={item.fieldID} value={item.fieldID}>
                {item.fieldName}
              </option>
            )
          })}
        </>
      </SelectBoxSimple>
      <span>=</span>
    </>
  )

  return (
    <>
      <HeaderPanel className={styles.dropdownHeader}>
        <Panel>
          <ButtonRow>
            <SearchInput
              onChange={(value) => {
                setSearchTerm(value)
                setActivePage(1)
              }}
              delay={50}
            >
              <>
                {count !== total ? `${numeral(count).format('0,0')}/` : ''}
                {numeral(total).format('0,0')} option
                {total > 1 ? 's' : ''}
              </>
            </SearchInput>
          </ButtonRow>
        </Panel>
      </HeaderPanel>
      <Table className={styles.table} inline>
        {isMobile && (
          <>
            {restrictDropdowns && <HeaderCell>{SelectParent}</HeaderCell>}
            <HeaderCell>
              <Button
                type="flatPink"
                align="left"
                onClick={() => {
                  setShowAdd(!showAdd)
                }}
              >
                Add dropdown option
              </Button>
            </HeaderCell>
          </>
        )}
        {!isMobile && (
          <Head className={styles.tableHeader}>
            <HeaderCell
              className={styles.headerCellWithButton}
              width={restrictDropdowns ? 340 : 503}
            >
              <Button
                className={styles.headerButton}
                onClick={(): void => {
                  if (initialSort) {
                    setInitialSort(false)
                  }
                  setSortOrder('optionName')
                }}
                type="none"
              >
                <span>Dropdown option name</span>
                <OrderArrow
                  className={styles.orderArrow}
                  currentKey="optionName"
                  sortKey={sortKey}
                  orderAsc={orderAsc}
                />
                <ToolTip
                  showUnder
                  className={classNames(styles.tooltip, styles.headerTooltip)}
                  message="The name users can select on Track>Create page dropdown."
                />
              </Button>
              <Button
                type="flatPink"
                align="left"
                onClick={() => {
                  setShowAdd(!showAdd)
                }}
              >
                Add
              </Button>
            </HeaderCell>
            <HeaderCell width={restrictDropdowns ? 340 : 503}>
              <Button
                className={styles.headerButton}
                onClick={(): void => {
                  if (initialSort) {
                    setInitialSort(false)
                  }
                  setSortOrder('optionValue')
                }}
                type="none"
              >
                Code
                <OrderArrow
                  className={styles.orderArrow}
                  currentKey="optionValue"
                  sortKey={sortKey}
                  orderAsc={orderAsc}
                />
                <ToolTip
                  showUnder
                  className={classNames(styles.tooltip, styles.headerTooltip)}
                  message="The value used in the URL when the option is selected."
                />
              </Button>
            </HeaderCell>
            {restrictDropdowns && (
              <HeaderCell width={340}>{SelectParent}</HeaderCell>
            )}
            <HeaderCell width={120}>
              Actions
              <ToolTip
                showOnLeft
                className={classNames(
                  styles.tooltip,
                  styles.headerTooltip,
                  styles.tooltipLeft,
                )}
                message={
                  <>
                    <div>
                      <img src={hide} alt="hide" />
                      <p
                        style={{
                          display: 'inline-block',
                          margin: '0 10px 5px',
                        }}
                      >
                        Temporarily hide this name and value.
                      </p>
                    </div>
                    <div>
                      <img src={hidden} alt="hidden" />
                      <p
                        style={{
                          display: 'inline-block',
                          margin: '0 10px 5px',
                        }}
                      >
                        Unhide name and value.
                      </p>
                    </div>
                    <div>
                      <img src={deleteImg} alt="delete" />
                      <p
                        style={{
                          display: 'inline-block',
                          margin: '0 10px 5px',
                        }}
                      >
                        Remove this name and value permanently.
                      </p>
                    </div>
                  </>
                }
              />
            </HeaderCell>
          </Head>
        )}
        <Body>
          {showAdd && (
            <>
              <AddNewSelectField
                fieldID={fieldID}
                restrictDropdowns={restrictDropdowns}
                fields={fields}
                key={shortid.generate()}
                fieldName={fieldName}
                updateSelectFields={onUpdateField}
                validation={validation}
              />
            </>
          )}
          {orderedFields &&
            orderedFields[activePage - 1] &&
            [...orderedFields[activePage - 1]].map(
              (item, index: number): React.ReactElement => {
                const { optionName, optionValue, optionID, optionFilter } = item
                const restrictDropdownsValue =
                  optionFilter && optionFilter.length > 0
                    ? optionFilter[0].parentOptionIDs
                    : []
                return (
                  <BodyItem
                    key={`${fieldName}${optionName}${optionValue}${optionID}`}
                    className={styles.bodyItem}
                  >
                    <Cell width={restrictDropdowns ? 340 : 503}>
                      <ClickEditInput
                        hide={item.hide}
                        id={`${fieldName} ${optionName} name`}
                        name={`${fieldName} ${optionName} name`}
                        value={optionName}
                        onChange={(value: string): void => {
                          // eslint-disable-next-line
                          // debugger
                          const updatedField = {
                            ...item,
                            optionName: value,
                          }
                          onUpdateField(updatedField, index)
                          dispatch(
                            updateGeneratorParameterSelectUpdateAsync({
                              fieldID,
                              updatedOption: updatedField,
                            }),
                          )
                        }}
                      />
                    </Cell>
                    <Cell width={restrictDropdowns ? 340 : 503}>
                      <ClickEditInput
                        hide={item.hide}
                        id={`${fieldName} ${optionValue} value`}
                        name={`${fieldName} ${optionValue} value`}
                        value={optionValue}
                        beforeChange={(value: string) => {
                          // return prepareInput(value, validation)
                          return prepareInput(value)
                        }}
                        onChange={(value: string): void => {
                          const updatedField = {
                            ...item,
                            optionValue: value,
                          }
                          onUpdateField(updatedField, index)
                          dispatch(
                            updateGeneratorParameterSelectUpdateAsync({
                              fieldID,
                              updatedOption: updatedField,
                            }),
                          )
                        }}
                      />
                    </Cell>
                    {restrictDropdowns && (
                      <Cell width={340}>
                        {parentFieldID !== '' && (
                          <MultiSelectBox
                            valsToCopy={copy}
                            showTags
                            typeAhead
                            showNoneWithTypeAhead
                            key={`${fieldName} ${optionName} ${optionValue}`}
                            id={`${fieldName} ${optionName} select`}
                            className={styles.multiSelect}
                            value={restrictDropdownsValue}
                            items={
                              selectedParent && selectedParent.selectFields
                                ? selectedParent.selectFields
                                : []
                            }
                            emptyTagsPlaceholder="Always show"
                            keyName="optionName"
                            keyValue="optionID"
                            onChange={(val) => {
                              const updatedField = {
                                ...item,
                              }
                              updatedField.optionFilter = [
                                {
                                  parentFieldID,
                                  parentOptionIDs: val,
                                },
                              ]
                              updateGeneratorParam(updatedField)
                              dispatch(
                                updateGeneratorParameterSelectUpdateAsync({
                                  fieldID,
                                  updatedOption: updatedField,
                                }),
                              )
                            }}
                          />
                        )}
                      </Cell>
                    )}
                    <Cell width={120}>
                      <Button
                        type={item.hide ? 'hidden' : 'hide'}
                        align="left"
                        onClick={() => {
                          const updatedField = {
                            ...item,
                            hide: !item.hide,
                          }

                          updateGeneratorParam(updatedField)
                          dispatch(
                            updateGeneratorParameterSelectUpdateAsync({
                              fieldID,
                              updatedOption: updatedField,
                            }),
                          )
                        }}
                      />
                      <DeleteButtonWithConfirmation
                        odd={!showAdd ? !(index % 2) : !!(index % 2)}
                        onClick={(): void => {
                          dispatch(
                            updateGeneratorParameterSelectDeleteAsync({
                              fieldID,
                              optionID,
                            }),
                          )
                          onDelete(optionName)
                        }}
                      >
                        <p>Are you sure you want to remove this row?</p>
                      </DeleteButtonWithConfirmation>
                    </Cell>
                  </BodyItem>
                )
              },
            )}
        </Body>
      </Table>
      {pages > 1 && (
        <ButtonRow className={styles.paginationRow}>
          <Pagination
            pages={pages}
            activePage={activePage}
            onChange={(index: number) => {
              setActivePage(index)
            }}
          />
        </ButtonRow>
      )}
      {active && (
        <Modal active={active} toggleActive={toggleActive} type="white" narrow>
          <ModalHead>
            <Heading type={3} align="left">
              Remove dropdown restrictions
            </Heading>
          </ModalHead>
          <ModalBody>
            <p>
              Are you sure you want to remove all the dropdown restrictions?
            </p>
          </ModalBody>
          <ModalFooter>
            <ConfirmationButtons
              type="simple"
              onYes={(): void => {
                setActive(false)
                // clear all the values
                const updatedFields = fields.map((item, index: number) => {
                  return {
                    ...item,
                    optionFilter: [],
                  }
                })
                updateGeneratorParam(updatedFields)
              }}
              onNo={(): void => {
                setActive(false)
              }}
              yesLabel="Yes"
              noLabel="Cancel"
            />
          </ModalFooter>
        </Modal>
      )}
    </>
  )
}
